import { useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { formatISO } from 'date-fns';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { z } from 'zod';

import { addToast } from '@/app/slices/toast';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { SslQcData, SslQcRequest } from '@/services/semen-pull/types';

import {
  useSaveSslQcMutation,
  useSslQcQuery,
} from '../../../services/semen-pull/hooks';

const sslQcSchema = z.object({
  qcHoeschtLot: z.string().nullable().optional(),
  qcTrisLot: z.string().nullable().optional(),
  sheathFluidDate: z.date().nullable().optional(),
  fdcLot: z.string().nullable().optional(),
  qcOvaHepesLot: z.string().nullable().optional(),
  mofloNum: z.string().nullable().optional(),
});

type SslQcFormValues = z.infer<typeof sslQcSchema>;

type SslQcDialogProps = {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  date: string | null;
  siteId: string | null;
};

function toDate(val: string | null | undefined): Date | null {
  if (!val) return null;
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? null : d;
}

function mapDataToForm(data: SslQcData | null | undefined): SslQcFormValues {
  return {
    qcHoeschtLot: data?.qcHoeschtLot ?? '',
    qcTrisLot: data?.qcTrisLot ?? '',
    sheathFluidDate: toDate(data?.sheathFluidDate),
    fdcLot: data?.fdcLot ?? '',
    qcOvaHepesLot: data?.qcOvaHepesLot ?? '',
    mofloNum: data?.mofloNum ?? '',
  };
}

export const SslQcDialog = ({
  visible,
  setVisible,
  date,
  siteId,
}: SslQcDialogProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { data: existingQcData } = useSslQcQuery({
    date,
    siteId,
  });

  const saveMutation = useSaveSslQcMutation();

  const { control, handleSubmit, reset } = useForm<SslQcFormValues>({
    resolver: zodResolver(sslQcSchema),
    defaultValues: mapDataToForm(existingQcData),
  });

  useEffect(() => {
    if (visible) {
      reset(mapDataToForm(existingQcData));
    }
  }, [visible, existingQcData, reset]);

  const onSubmit = async (formData: SslQcFormValues) => {
    if (!date || !siteId) return;

    const request: SslQcRequest = {
      date: formatISO(new Date(date), { representation: 'date' }),
      siteId,
      qcHoeschtLot: formData.qcHoeschtLot || null,
      qcTrisLot: formData.qcTrisLot || null,
      sheathFluidDate: formData.sheathFluidDate?.toISOString() ?? null,
      fdcLot: formData.fdcLot || null,
      qcOvaHepesLot: formData.qcOvaHepesLot || null,
      mofloNum: formData.mofloNum || null,
    };

    try {
      await saveMutation.mutateAsync(request);
      dispatch(
        addToast({
          severity: 'success',
          summary: t('common.success'),
          detail: 'SSL QC data saved successfully.',
          sticky: false,
        }),
      );
      setVisible(false);
    } catch {
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.error'),
          detail: 'Failed to save SSL QC data.',
          sticky: false,
        }),
      );
    }
  };

  return (
    <Dialog
      open={visible}
      onOpenChange={(open) => {
        if (!open) {
          reset();
          setVisible(false);
        }
      }}
    >
      <DialogContent className="md:max-w-lg">
        <DialogHeader>
          <DialogTitle>SSL QC Information</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <FieldGroup className="gap-4">
            <Field>
              <FieldLabel htmlFor="qcHoeschtLot">
                QC Hoescht Expiration Date
              </FieldLabel>
              <Controller
                name="qcHoeschtLot"
                control={control}
                render={({ field }) => (
                  <Input
                    id="qcHoeschtLot"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                )}
              />
            </Field>

            <Field>
              <FieldLabel htmlFor="qcTrisLot">
                QC Tris A pH 7.4 Expiration Date
              </FieldLabel>
              <Controller
                name="qcTrisLot"
                control={control}
                render={({ field }) => (
                  <Input
                    id="qcTrisLot"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                )}
              />
            </Field>

            <Field>
              <FieldLabel htmlFor="sheathFluidDate">
                QC Sheath Fluid Manufacturing Date
              </FieldLabel>
              <Controller
                name="sheathFluidDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    value={field.value ?? undefined}
                    onChange={(val) => field.onChange(val ?? null)}
                  />
                )}
              />
            </Field>

            <Field>
              <FieldLabel htmlFor="fdcLot">QC FD&C Expiration Date</FieldLabel>
              <Controller
                name="fdcLot"
                control={control}
                render={({ field }) => (
                  <Input
                    id="fdcLot"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                )}
              />
            </Field>

            <Field>
              <FieldLabel htmlFor="qcOvaHepesLot">
                QC OvaHepes Expiration Date
              </FieldLabel>
              <Controller
                name="qcOvaHepesLot"
                control={control}
                render={({ field }) => (
                  <Input
                    id="qcOvaHepesLot"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                )}
              />
            </Field>

            <Field>
              <FieldLabel htmlFor="mofloNum">QC Moflo #</FieldLabel>
              <Controller
                name="mofloNum"
                control={control}
                render={({ field }) => (
                  <Input
                    id="mofloNum"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                )}
              />
            </Field>
          </FieldGroup>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => {
              reset();
              setVisible(false);
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={saveMutation.isPending}
          >
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
